export const REWARD_ACTIVITY_CACHE_TTL_MS = 5 * 60 * 1000

export type RewardActivityLoadMode = 'blocking' | 'fresh' | 'background'

export function getRewardActivityLoadMode(
  loadedAt: number,
  now: number = Date.now(),
): RewardActivityLoadMode {
  if (!loadedAt) return 'blocking'
  return (now - loadedAt) < REWARD_ACTIVITY_CACHE_TTL_MS ? 'fresh' : 'background'
}

export function isDeliveredRewardActivity(row: any): boolean {
  return !!row && (row.status === 'Y' || row.status_send === 'SC')
}

function memberKey(row: any): string {
  const email = String((row && row.email) || '').trim().toLowerCase()
  if (email) return email
  return String((row && row.user) || '').trim().toLowerCase()
}

function currencyKey(row: any): string {
  return String((row && row.reward_currency) || (row && row.symbol) || 'Unknown')
}

function activityTimestamp(row: any): number {
  const value = row && (row.date_raw || row.updated)
  const timestamp = value ? new Date(value).getTime() : 0
  return Number.isFinite(timestamp) ? timestamp : 0
}

export function buildRewardCurrencyTotals(rows: any[]): any[] {
  const totals: Record<string, any> = {}

  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!isDeliveredRewardActivity(row)) return

    const amount = Number(row.value) || 0
    if (amount <= 0) return
    const currency = currencyKey(row)
    if (!totals[currency]) {
      totals[currency] = {
        currency,
        symbol: row.symbol || '',
        amount: 0,
        redemptions: 0,
      }
    }
    totals[currency].amount += amount
    totals[currency].redemptions += 1
  })

  return Object.keys(totals)
    .map((key) => totals[key])
    .sort((a, b) => b.amount - a.amount || a.currency.localeCompare(b.currency))
}

export function buildMemberRewardSpend(rows: any[]): any[] {
  const currencyTotals = buildRewardCurrencyTotals(rows).reduce((result, item) => {
    result[item.currency] = item.amount
    return result
  }, {} as Record<string, number>)
  const members: Record<string, any> = {}

  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!isDeliveredRewardActivity(row)) return

    const amount = Number(row.value) || 0
    if (amount <= 0) return

    const person = memberKey(row)
    if (!person) return

    const currency = currencyKey(row)
    const key = `${person}::${currency}`
    const timestamp = activityTimestamp(row)
    if (!members[key]) {
      members[key] = {
        key,
        name: row.user || row.email || 'Unknown member',
        email: row.email || '',
        team: row.user_team || '',
        pic: row.user_pic || '',
        currency,
        symbol: row.symbol || '',
        amount: 0,
        redemptions: 0,
        lastActivity: row.date || '',
        lastActivityRaw: row.date_raw || row.updated || '',
        lastActivityTimestamp: timestamp,
      }
    }

    const member = members[key]
    member.amount += amount
    member.redemptions += 1
    if (timestamp >= member.lastActivityTimestamp) {
      member.lastActivity = row.date || member.lastActivity
      member.lastActivityRaw = row.date_raw || row.updated || member.lastActivityRaw
      member.lastActivityTimestamp = timestamp
    }
  })

  return Object.keys(members)
    .map((key) => {
      const member = members[key]
      const total = currencyTotals[member.currency] || 0
      return {
        ...member,
        currencyTotal: total,
        share: total > 0 ? (member.amount / total) * 100 : 0,
      }
    })
    .sort((a, b) => {
      if (a.currency !== b.currency) {
        return b.currencyTotal - a.currencyTotal || a.currency.localeCompare(b.currency)
      }
      return b.amount - a.amount || b.redemptions - a.redemptions || a.name.localeCompare(b.name)
    })
}
